import React, { useState, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_SHIPS, GET_FLAG_NATIONS, GET_KINDS } from '../graphql/queries';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import './ShipSearch.css';

const ShipSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    nameContains: '',
    flagNationId: '',
    kindId: '',
    huge: null,
    lengthMin: '',
    lengthMax: '',
    tonnageMin: '',
    tonnageMax: ''
  });

  const [searchResult, setSearchResult] = useState(null);
  const [flagNationFilter, setFlagNationFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [currentCursor, setCurrentCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const resultsRef = useRef(null);

  const { loading, error, data, refetch } = useQuery(GET_SHIPS, {
    variables: {
      ...searchParams,
      first: 20,
      after: currentCursor
    },
    skip: true // 初期状態では検索を実行しない
  });

  const { data: flagNationsData } = useQuery(GET_FLAG_NATIONS, {
    variables: { nameContains: flagNationFilter },
  });

  const { data: kindsData } = useQuery(GET_KINDS, {
    variables: { nameContains: kindFilter },
  });


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    // 空の値をnullに変換
    const cleanedParams = Object.fromEntries(
      Object.entries(searchParams).map(([key, value]) => [
        key,
        value === '' ? null : value
      ])
    );
    setCurrentCursor(null);
    setCursorHistory([]);
    const result = await refetch({
      ...cleanedParams,
      first: 20,
      after: null
    });
    setSearchResult(result.data);
  };

  const handleClear = () => {
    setSearchParams({
      nameContains: '',
      flagNationId: '',
      kindId: '',
      huge: null,
      lengthMin: '',
      lengthMax: '',
      tonnageMin: '',
      tonnageMax: ''
    });
    setSearchResult(null);
    setCurrentCursor(null);
    setCursorHistory([]);
  };

  const handleNextPage = async () => {
    if (!searchResult?.getShips?.pageInfo?.hasNextPage) return;
    const nextCursor = searchResult.getShips.pageInfo.endCursor;
    setCursorHistory(prev => [...prev, currentCursor]);
    setCurrentCursor(nextCursor);
    const cleanedParams = Object.fromEntries(
      Object.entries(searchParams).map(([key, value]) => [
        key,
        value === '' ? null : value
      ])
    );
    const result = await refetch({
      ...cleanedParams,
      first: 20,
      after: nextCursor
    });
    setSearchResult(result.data);
    // 検索結果の先頭にスクロール
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handlePreviousPage = async () => {
    if (cursorHistory.length === 0) return;
    const previousCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory(prev => prev.slice(0, -1));
    setCurrentCursor(previousCursor);
    const cleanedParams = Object.fromEntries(
      Object.entries(searchParams).map(([key, value]) => [
        key,
        value === '' ? null : value
      ])
    );
    const result = await refetch({
      ...cleanedParams,
      first: 20,
      after: previousCursor
    });
    setSearchResult(result.data);
    // 検索結果の先頭にスクロール
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleShipClick = (shipId) => {
    navigate(`/ship/${shipId}`);
  };

  const getShipTypeIcon = (kind) => {
    const defaultIcon = '/static/default-ship-icon.png';

    // アイコンURLが提供されている場合はそれを使用
    if (kind?.iconUrl) {
      return (
        <img
          src={kind.iconUrl}
          alt={kind.name || '船種'}
          className="ship-type-icon"
          onError={(e) => {
            // 画像の読み込みに失敗した場合はデフォルト画像を表示
            e.target.src = defaultIcon;
          }}
        />
      );
    }

    // iconUrlがない場合はデフォルト画像を表示
    return (
      <img
        src={defaultIcon}
        alt={kind?.name || '船種'}
        className="ship-type-icon"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '未定';
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  return (
    <div className="ship-search">
      <div className="ship-search-header">
        <button
          className="back-button"
          onClick={() => navigate('/')}
        >
          ← 一覧に戻る
        </button>
        <h1>船舶検索</h1>
      </div>

      <div className="ship-search-content">
        <div className="search-form-container">
          <form className="search-form" onSubmit={handleSearch}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nameContains">船名（部分一致）</label>
                <input
                  type="text"
                  id="nameContains"
                  name="nameContains"
                  value={searchParams.nameContains}
                  onChange={handleInputChange}
                  placeholder="船名を入力"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="flagNationId">旗国</label>
                <input
                  type="text"
                  placeholder="旗国を検索"
                  value={flagNationFilter}
                  onChange={(e) => setFlagNationFilter(e.target.value)}
                />
                <select
                  id="flagNationId"
                  name="flagNationId"
                  value={searchParams.flagNationId}
                  onChange={handleInputChange}
                >
                  <option value="">全ての旗国</option>
                  {flagNationsData?.getFlagNations?.map((nation) => (
                    <option key={nation.id} value={nation.id}>
                      {nation.name}
                      {nation.abbreviation && ` (${nation.abbreviation})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="kindId">船種</label>
                <input
                  type="text"
                  placeholder="船種を検索"
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                />
                <select
                  id="kindId"
                  name="kindId"
                  value={searchParams.kindId}
                  onChange={handleInputChange}
                >
                  <option value="">全ての船種</option>
                  {kindsData?.getKinds?.map((kind) => (
                    <option key={kind.id} value={kind.id}>
                      {kind.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="lengthMin">長さ（最小）</label>
                <input
                  type="number"
                  id="lengthMin"
                  name="lengthMin"
                  value={searchParams.lengthMin}
                  onChange={handleInputChange}
                  placeholder="最小長さ（m）"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lengthMax">長さ（最大）</label>
                <input
                  type="number"
                  id="lengthMax"
                  name="lengthMax"
                  value={searchParams.lengthMax}
                  onChange={handleInputChange}
                  placeholder="最大長さ（m）"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="tonnageMin">トン数（最小）</label>
                <input
                  type="number"
                  id="tonnageMin"
                  name="tonnageMin"
                  value={searchParams.tonnageMin}
                  onChange={handleInputChange}
                  placeholder="最小トン数"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="tonnageMax">トン数（最大）</label>
                <input
                  type="number"
                  id="tonnageMax"
                  name="tonnageMax"
                  value={searchParams.tonnageMax}
                  onChange={handleInputChange}
                  placeholder="最大トン数"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="huge"
                    checked={searchParams.huge === true}
                    onChange={(e) => setSearchParams(prev => ({
                      ...prev,
                      huge: e.target.checked ? true : null
                    }))}
                  />
                  巨大船のみ
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="search-button">
                🔍 検索
              </button>
              <button type="button" className="clear-button" onClick={handleClear}>
                🗑️ クリア
              </button>
            </div>
          </form>
        </div>

        <div className="search-results" ref={resultsRef}>
          {loading && <LoadingSpinner />}
          {error && <ErrorMessage message="検索に失敗しました" />}

          {searchResult && searchResult.getShips?.nodes && (() => {
            const currentPage = cursorHistory.length + 1;
            const startItem = (currentPage - 1) * 20 + 1;
            const endItem = (currentPage - 1) * 20 + searchResult.getShips.nodes.length;
            return (
              <div className="results-header">
                <h2>検索結果（{startItem}-{endItem}件）</h2>
              </div>
            );
          })()}

          {searchResult && searchResult.getShips?.nodes && searchResult.getShips.nodes.length > 0 ? (
            <>
              <div className="ships-grid">
                {searchResult.getShips.nodes.map((ship) => (
                  <div
                    key={ship.id}
                    className="ship-card"
                    onClick={() => handleShipClick(ship.id)}
                  >
                    <div className="ship-card-header">
                      <div className="ship-icon">
                        {getShipTypeIcon(ship.kind)}
                      </div>
                      <div className="ship-info">
                        <h3 className="ship-name clickable">
                          {ship.name || '船名不明'}
                        </h3>
                        <p className="ship-type">{ship.kind?.name || '船種不明'}</p>
                      </div>
                      <div className="ship-badges">
                        {ship.huge && <span className="huge-flag">巨大船</span>}
                      </div>
                    </div>

                    <div className="ship-details">
                      <div className="detail-row">
                        <span className="detail-label">旗国:</span>
                        <span className="detail-value flag-nation">
                          {ship.flagNation?.imageUrl && (
                            <img
                              src={ship.flagNation.imageUrl}
                              alt={ship.flagNation.name || '旗国'}
                              className="flag-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <span className="flag-text">
                            {ship.flagNation?.name || '不明'}
                            {ship.flagNation?.abbreviation && ` (${ship.flagNation.abbreviation})`}
                          </span>
                        </span>
                      </div>

                      {ship.length && (
                        <div className="detail-row">
                          <span className="detail-label">長さ:</span>
                          <span className="detail-value">{ship.length}m</span>
                        </div>
                      )}

                      {ship.tonnage && (
                        <div className="detail-row">
                          <span className="detail-label">トン数:</span>
                          <span className="detail-value">{ship.tonnage.toLocaleString()}t</span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="detail-label">航行情報:</span>
                        <span className="detail-value">
                          {ship.passagePlans?.nodes?.length || 0}件
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">作成日:</span>
                        <span className="detail-value">{formatDate(ship.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pagination">
                <button
                  className="pagination-button"
                  onClick={handlePreviousPage}
                  disabled={cursorHistory.length === 0}
                >
                  ← 前へ
                </button>
                <span className="pagination-info">
                  {searchResult.getShips.nodes.length}件表示
                </span>
                <button
                  className="pagination-button"
                  onClick={handleNextPage}
                  disabled={!searchResult.getShips.pageInfo?.hasNextPage}
                >
                  次へ →
                </button>
              </div>
            </>
          ) : searchResult && searchResult.getShips?.nodes && searchResult.getShips.nodes.length === 0 ? (
            <div className="no-results">
              <p>検索条件に一致する船舶が見つかりませんでした</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ShipSearch;
